import React, { useState, useEffect } from 'react';
import { logoutAdmin } from '../../utils/auth';
import { 
  getSubmissions, 
  updateSubmissionStatus, 
  deleteSubmission, 
  filterSubmissions, 
  exportSubmissions,
  Submission 
} from '../../utils/storage';

interface AdminDashboardProps {
  onLogout: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<Submission[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [filters, setFilters] = useState({
    type: 'all' as 'all' | 'project' | 'resource',
    status: 'all' as 'all' | 'new' | 'reviewed' | 'completed',
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [submissions, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSubmissions = () => {
    const allSubmissions = getSubmissions();
    setSubmissions(allSubmissions);
  };

  const applyFilters = () => {
    const filtered = filterSubmissions(submissions, filters);
    setFilteredSubmissions(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleStatusChange = async (id: string, newStatus: Submission['status']) => {
    const success = updateSubmissionStatus(id, newStatus);
    if (success) {
      loadSubmissions();
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(prev => prev ? { ...prev, status: newStatus } : null);
      }
    }
  };

  const handleDeleteSubmission = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      const success = deleteSubmission(id);
      if (success) {
        loadSubmissions();
        if (selectedSubmission?.id === id) {
          setSelectedSubmission(null);
        }
      }
    }
  };

  const handleExport = (type: 'all' | 'filtered') => {
    const dataToExport = type === 'all' ? submissions : filteredSubmissions;
    const jsonData = exportSubmissions(dataToExport);
    
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `submissions_${type}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleLogout = () => {
    logoutAdmin();
    onLogout();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'reviewed': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'project' 
      ? 'bg-purple-500/20 text-purple-400 border-purple-500/30'
      : 'bg-orange-500/20 text-orange-400 border-orange-500/30';
  };

  return (
    <div className="min-h-screen bg-surface text-ink-700">
      {/* Header */}
      <div className="bg-surface-soft border-b border-white/10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-ink-900">Admin Dashboard</h1>
              <p className="text-ink-500">Manage partner submissions</p>
            </div>
            <button
              onClick={handleLogout}
              className="btn-secondary"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="glass-card p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ink-500">Total</p>
                <p className="text-2xl font-bold text-ink-900">{submissions.length}</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ink-500">New</p>
                <p className="text-2xl font-bold text-ink-900">{submissions.filter(s => s.status === 'new').length}</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ink-500">Projects</p>
                <p className="text-2xl font-bold text-ink-900">{submissions.filter(s => s.type === 'project').length}</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ink-500">Resources</p>
                <p className="text-2xl font-bold text-ink-900">{submissions.filter(s => s.type === 'resource').length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="glass-card p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-ink-900 mb-2">Type</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none"
              >
                <option value="all">All</option>
                <option value="project">Project</option>
                <option value="resource">Resource</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-900 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none"
              >
                <option value="all">All</option>
                <option value="new">New</option>
                <option value="reviewed">Reviewed</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-900 mb-2">From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-900 mb-2">To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-ink-900 mb-2">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search company, contact, email..."
                className="w-full px-3 py-2 border border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleExport('filtered')}
              className="btn-secondary text-sm"
            >
              Export Filtered ({filteredSubmissions.length})
            </button>
            <button
              onClick={() => handleExport('all')}
              className="btn-secondary text-sm"
            >
              Export All ({submissions.length})
            </button>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-soft">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-white/5">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm text-ink-500">{submission.id.slice(-8)}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTypeColor(submission.type)}`}>
                        {submission.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-ink-900">
                        {submission.data.companyName}
                      </div>
                      <div className="text-sm text-ink-500">
                        {submission.type === 'project' ? submission.data.projectName : submission.data.projectTeamName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-ink-900">
                        {submission.data.contactPerson || 'N/A'}
                      </div>
                      <div className="text-sm text-ink-500">
                        {submission.data.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                      {formatDate(submission.submittedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={submission.status}
                        onChange={(e) => handleStatusChange(submission.id, e.target.value as Submission['status'])}
                        className={`text-xs font-semibold rounded border px-2 py-1 bg-transparent ${getStatusColor(submission.status)}`}
                      >
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedSubmission(submission)}
                          className="text-brand-start hover:text-brand-end transition-colors"
                          title="View Details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteSubmission(submission.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredSubmissions.length === 0 && (
              <div className="text-center py-12">
                <p className="text-ink-500">No submissions found</p>
              </div>
            )}
          </div>
        </div>

        {/* Submission Detail Modal */}
        {selectedSubmission && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="glass-card max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl">
              <div className="p-6 border-b border-white/10">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-ink-900">
                    Submission Details - {selectedSubmission.id}
                  </h2>
                  <button
                    onClick={() => setSelectedSubmission(null)}
                    className="text-ink-500 hover:text-ink-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-ink-500 mb-1">Type</h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTypeColor(selectedSubmission.type)}`}>
                        {selectedSubmission.type}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-ink-500 mb-1">Status</h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(selectedSubmission.status)}`}>
                        {selectedSubmission.status}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-ink-500 mb-1">Submitted At</h3>
                      <p className="text-ink-900">{formatDate(selectedSubmission.submittedAt)}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-ink-500 mb-1">Submission ID</h3>
                      <code className="text-ink-900">{selectedSubmission.id}</code>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-ink-900 mb-4">Form Data</h3>
                    <pre className="bg-surface-soft p-4 rounded-lg overflow-x-auto text-sm text-ink-700">
                      {JSON.stringify(selectedSubmission.data, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
